import React, { useContext, useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Dropdown,
  Form,
  Layout,
  Menu,
  message,
  Modal,
  // notification,
  PageHeader,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Spin,
} from 'antd';
import { useHistory, useParams } from 'react-router';
import { Field, Grid, GridCell, GridRow, Integration, IntegrationExecution, GridLayout, GridLayoutColumn, Condition } from '../../models';
import {
  addSocketHandler,
  apiClient,
  apiErrorMessage,
  generateRandomString,
  hasViewProcessingRecordsPermission,
  hasCreateRecordPermission,
  hasExportToExcelPermission,
  removeSocketHandler,
} from '../../utils';
import {
  BorderOutlined,
  BulbOutlined,
  CheckSquareOutlined,
  CommentOutlined,
  DisconnectOutlined,
  EditOutlined,
  EllipsisOutlined,
  FilePdfOutlined,
  FilterOutlined,
  LoadingOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { NewGridRowPage } from './new-grid-row-page';
import { GridLayoutsForm } from './grid-layouts-form';
import { CellEditPage } from './cell-edit-page';
import { GridColumnFilter } from './grid-column-filter';
import { RowDetailsPage } from './row-details-page';
import { GridCellContent } from './grid-cell';
import { CellStatusIndicator } from './cell-status-indicator';
import { useForm } from 'antd/lib/form/Form';
import { ImportGridRowsPage } from './import-grid-rows-page';
import { ExcelExportPage } from './excel-export-page';
import moment from 'moment';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { UserContext } from '../contexts';
import { useLocation } from 'react-router-dom';
import { ItemJigNewRecordPage } from './custom/item-jig-new-record-page';
import { FloatingModal } from '../../elements';

export function GridPage(props: { gridId?: string; rowId?: string; onChange?: () => void }) {
  const { user } = useContext(UserContext);

  let { id } = useParams() as any;
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const recordId = useRef(search.get('recordid'));
  const fieldId = useRef(search.get('fieldid'));
  const withMyTasks = useRef(search.get('withMyTasks'));

  const { Content } = Layout;
  const { Text } = Typography;

  const history = useHistory();
  const [gridLoading, setGridLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [parentGridLoading, setParentGridLoading] = useState(false);
  const [grid, setGrid] = useState<Grid>();
  const [showAuditColumns, setShowAuditColumns] = useState(false);
  const [tableColumns, setTableColumns] = useState<any[]>();
  const [rows, setRows] = useState<GridRow[]>();
  const [addRowModalOpen, setAddRowModalOpen] = useState(false);
  const [importRowsModalOpen, setImportRowsModalOpen] = useState(false);
  const [excelExportModalOpen, setExcelExportModalOpen] = useState(false);
  const [rowDetailsModalOpen, setRowDetailsModalOpen] = useState<any>();
  const [cellDetailsModalOpen, setCellDetailsModalOpen] = useState<any>();
  const [showTasks, setShowTasks] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deepestLevel, setDeepestLevel] = useState(1);
  const [selectable, setSelectable] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GridRow[]>();
  const [selectedAll, setSelectedAll] = useState(false);
  const [integrations, setIntegrations] = useState<Integration[]>();
  const [hasActiveOperation, setHasActiveOperation] = useState(false);
  const [filterResetTrigger, setFilterResetTrigger] = useState(0);
  const [forceAllRevisions, setForceAllRevisions] = useState(false);
  const [activeRecordId, setActiveRecordId] = useState<string>();
  const [expandedRowKeysUi, setExpandedRowKeysUi] = useState<string[]>();
  const expandedRowKeys = useRef<string[]>([]);

  const [defaultGridLayout, setDefaultGridLayout] = useState<GridLayout>();
  const [gridLayouts, setGridLayouts] = useState<GridLayout[]>();
  const [activeColumns, setActiveColumns] = useState<GridLayoutColumn[]>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableOrderBy, setTableOrderBy] = useState<string>();
  const [tableOrderDir, setTableOrderDir] = useState<'ASC' | 'DESC'>();
  const [dataElementFilters, setDataElementFilters] = useState<any>();

  const rowsRef = useRef<GridRow[]>();
  const gridRef = useRef<Grid>();
  const dataSetFiltersRef = useRef<any>();
  const dataElementFiltersRef = useRef<any>();
  const tableOrderByRef = useRef<string>();
  const tableOrderDirRef = useRef<'ASC' | 'DESC'>();
  const tableCurrentRef = useRef(1);
  const tablePageSizeRef = useRef(10);
  const tableColumnsRef = useRef<any[]>();

  const retryUpdatedEventInterval = useRef<any>();
  const storedRecordUpdatedEvents = useRef<any[]>([]);

  const [userCanCreateRecord, setUserCanCreateRecord] = useState(false);
  const [userCanExportToExcel, setUserCanExportToExcel] = useState(false);

  const onBackClick = () => {
    history.goBack();
  };
  // CUSTOM DEVELOPMENT
  useEffect(() => {
    if (
      (id !== '18c9e515-ab9b-47a4-8169-f974bba5992c' && id !== '8d19a009-e476-4f13-810d-cfc5f920cdc7') ||
      !user?.roles?.some(r => r.name === 'Kalite')
    ) {
      return;
    }
    setFiltersOpen(true);
  }, [id, user?.roles]);

  useEffect(() => {
    if (grid) {
      setUserCanCreateRecord(hasCreateRecordPermission(user, grid?.id));
      setUserCanExportToExcel(hasExportToExcelPermission(user, grid?.id));
    }

    if (grid?.parentGridId) {
      setParentGridLoading(true);
      apiClient
        .get(`grids/${grid?.parentGridId}`)
        .then((response: any) => {
          const parentGrid = response.data as Grid;
          const parentField = parentGrid?.fields?.find(f => f.id === grid?.parentFieldId);
          setShowAuditColumns(parentField?.showAuditColumns || false);
          setParentGridLoading(false);
        })
        .catch(() => {
          setParentGridLoading(false);
        });
    }
  }, [grid, user]);

  const loadGrid = useCallback(() => {
    setGridLoading(true);
    const gridId = props.gridId || id;
    apiClient
      .get(`grids/${gridId}`)
      .then((response: any) => {
        const responseGrid = response.data as Grid;
        setGrid(responseGrid);
        gridRef.current = responseGrid;
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setGridLoading(false));
  }, [id, props.gridId]);

  const loadRecords = useCallback(() => {
    console.log('Loading records...');
    setRowsLoading(true);

    let showAllRevisions = false;
    let offset = (tableCurrent - 1) * tablePageSize;
    offset = offset < 0 ? 0 : offset;
    const gridId = props.gridId || id;

    // Build request body instead of query params
    const requestBody: any = {
      gridId: gridId,
      limit: tablePageSize,
      offset: offset,
    };

    const conditions: Condition = {
      logic: 'AND',
      items: [],
    };

    if (props.rowId) {
      conditions.items.push({
        objectType: 'record',
        objectId: 'MDM_RECORD',
        operator: 'recordIds',
        value: [props.rowId],
      });
    }
    if (tableOrderBy && tableOrderDir) {
      requestBody.orderBy = tableOrderBy;
      requestBody.orderDir = tableOrderDir;
    }
    if (!!recordId.current && !!fieldId.current) {
      conditions.items.push({
        objectType: 'record',
        objectId: 'MDM_RECORD',
        operator: 'recordIds',
        value: [recordId.current],
      });
    } else {
      if (dataElementFilters) {
        for (let filterKey in dataElementFilters) {
          if (!dataElementFilters[filterKey]) {
            continue;
          }

          const filter = dataElementFilters[filterKey];

          const basicSearchFilterValue = filter[0];
          const isNullFilterValue = filter[1];
          const isNotFilterValue = filter[2];
          const isTaskFilterValue = filter[3];
          const isErrorFilterValue = filter[4];
          const advancedFilterValue = filter[5];
          const distinctFilterValue = filter[6];

          if (basicSearchFilterValue !== undefined && basicSearchFilterValue !== null && basicSearchFilterValue.toString().trim() !== '') {
            conditions.items.push({
              objectType: 'dataElement',
              objectId: filterKey,
              operator: 'search',
              value: basicSearchFilterValue,
            });
          }

          if (isNullFilterValue === '1') {
            conditions.items.push({
              objectType: 'dataElement',
              objectId: filterKey,
              operator: 'isNull',
              value: true,
            });
          }

          if (isNotFilterValue === '1') {
            conditions.items.push({
              objectType: 'dataElement',
              objectId: filterKey,
              operator: 'isNotNull',
              value: true,
            });
          }

          if (isTaskFilterValue === '1') {
            conditions.items.push({
              objectType: 'dataElement',
              objectId: filterKey,
              operator: 'tasks',
              value: 'true',
            });
          }

          if (isErrorFilterValue === '1') {
            conditions.items.push({
              objectType: 'dataElement',
              objectId: filterKey,
              operator: 'errors',
              value: 'true',
            });
          }

          if (distinctFilterValue) {
            let values = JSON.parse(distinctFilterValue)?.map((v: string | null) => (v === null ? '###null###' : v));
            conditions.items.push({
              objectType: 'dataElement',
              objectId: filterKey,
              operator: 'in',
              value: values,
            });
          }

          if (advancedFilterValue) {
            const advancedFilters = JSON.parse(advancedFilterValue);
            conditions.items.push(advancedFilters);
          }

          showAllRevisions = true;
        }
      }

      // CUSTOM DEVELOPMENT
      // if gridRef is null, the records are being loaded for the first time
      if (
        !gridRef.current &&
        (gridId === '18c9e515-ab9b-47a4-8169-f974bba5992c' || gridId === '8d19a009-e476-4f13-810d-cfc5f920cdc7') &&
        user?.roles?.some(r => r.name === 'Kalite')
      ) {
        dataSetFiltersRef.current = { taskStatus: 'WITH_MY_TASKS' };
      }

      if (!gridRef.current && withMyTasks.current === 'true') {
        dataSetFiltersRef.current = { taskStatus: 'WITH_MY_TASKS' };
        setFiltersOpen(true);
      }

      if (dataSetFiltersRef.current?.activeStatus && dataSetFiltersRef.current?.activeStatus !== 'ACTIVE') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'activeStatus',
          value: dataSetFiltersRef.current.activeStatus === 'INACTIVE' ? 'inactive' : 'all',
        });
        showAllRevisions = true;
      } else {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'activeStatus',
          value: 'active',
        });
      }

      if (dataSetFiltersRef.current?.taskStatus && dataSetFiltersRef.current?.taskStatus !== 'ALL') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: dataSetFiltersRef.current.taskStatus === 'WITH_MY_TASKS' ? 'myTasks' : 'tasks',
          value: dataSetFiltersRef.current.taskStatus === 'WITHOUT_TASKS' ? 'false' : 'true',
        });
        showAllRevisions = true;
      }

      if (dataSetFiltersRef.current?.errorStatus && dataSetFiltersRef.current?.errorStatus !== 'ALL') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'errors',
          value: dataSetFiltersRef.current.errorStatus === 'WITH_ERRORS' ? 'true' : 'false',
        });
        showAllRevisions = true;
      }

      if (dataSetFiltersRef.current?.lastUpdate && dataSetFiltersRef.current?.lastUpdate !== 'ALL') {
        let lastUpdateValue: string = '';
        let subtractDays = 0;
        switch (dataSetFiltersRef.current.lastUpdate) {
          case 'ONE_DAY':
            subtractDays = 1;
            break;
          case 'ONE_WEEK':
            subtractDays = 7;
            break;
          case 'TWO_WEEKS':
            subtractDays = 14;
            break;
          case 'ONE_MONTH':
            subtractDays = 30;
            break;
          case 'ONE_YEAR':
            subtractDays = 365;
            break;
        }
        if (subtractDays > 0) {
          lastUpdateValue = moment.utc().subtract(subtractDays, 'days').startOf('day').format('YYYY-MM-DD');
        }

        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'lastUpdatedAfter',
          value: lastUpdateValue,
        });
      }

      if (dataSetFiltersRef.current?.revisionStatus || showAllRevisions) {
        if (
          dataSetFiltersRef.current?.revisionStatus === 'PREVIOUS' ||
          dataSetFiltersRef.current?.revisionStatus === 'ALL' ||
          showAllRevisions
        ) {
          conditions.items.push({
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'revisions',
            value: dataSetFiltersRef.current?.revisionStatus === 'ALL' || showAllRevisions ? 'all' : 'previous',
          });
          showAllRevisions = true;
        } else {
          conditions.items.push({
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'revisions',
            value: 'LATEST',
          });

          if (dataSetFiltersRef.current?.revisionStatus === 'NESTED') {
            requestBody.includeRevisions = true;
          }

          showAllRevisions = false;
        }
      }

      if (dataSetFiltersRef.current?.recordStatus && dataSetFiltersRef.current?.recordStatus !== 'ALL') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'processStatus',
          value: dataSetFiltersRef.current.recordStatus === 'PROCESSING' ? 'processing' : 'ready',
        });
      }

      if (showTasks) {
        requestBody.includeTasks = true;
      }

      if (
        expandedRowKeys.current?.length &&
        (!dataSetFiltersRef.current || !dataSetFiltersRef.current.revisionStatus || dataSetFiltersRef.current?.revisionStatus === 'LATEST')
      ) {
        requestBody.includeRevisionsFor = expandedRowKeys.current;
      }
    }

    setForceAllRevisions(showAllRevisions);

    requestBody.conditions = conditions;

    apiClient
      .post(`records/records/query`, requestBody)
      .then((response: any) => {
        // const responseGrid = response.data.grid as Grid;

        const responseRows = response.data.records?.map((r: any) => {
          return {
            ...r,
            children: r.hasChildRows ? [] : undefined,
            revisionRecords:
              r.isRevision && !r.revisionRecords?.length && dataSetFiltersRef.current?.revisionStatus !== 'ALL' && !showAllRevisions
                ? r.hasOlderRevisions
                  ? []
                  : undefined
                : r.revisionRecords,
          };
        });

        // if (responseGrid) {
        //   setGrid(responseGrid);
        //   gridRef.current = responseGrid;
        // } else {
        //   loadGrid();
        // }

        if (dataSetFiltersRef.current?.revisionStatus === 'NESTED') {
          expandedRowKeys.current = responseRows.map((r: any) => r.id);
          setExpandedRowKeysUi(expandedRowKeys.current);
        }

        setRows(responseRows);
        rowsRef.current = responseRows;
        setTableTotal(response.data.count);

        // if (!!recordId.current && !!fieldId.current) {
        //   const field = responseGrid?.fields.find(f => f.id === fieldId.current);
        //   const record = responseRows?.find((r: GridRow) => r.id === recordId.current);
        //   if (!user?.roles?.some(r => r.name === 'Misafir')) {
        //     setCellDetailsModalOpen({ row: record, grid: responseGrid, field, rowId: recordId.current });
        //   }
        //   setActiveRecordId(recordId.current);
        //   recordId.current = null;
        //   fieldId.current = null;
        // }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setRowsLoading(false));
  }, [tableCurrent, tablePageSize, props.gridId, props.rowId, id, tableOrderBy, tableOrderDir, dataElementFilters, user?.roles, showTasks]);

  useEffect(() => {
    if (!!recordId.current && !!fieldId.current) {
      const field = grid?.fields.find(f => f.id === fieldId.current);
      const record = rows?.find((r: GridRow) => r.id === recordId.current);
      if (!user?.roles?.some(r => r.name === 'Misafir')) {
        setCellDetailsModalOpen({ row: record, grid, field, rowId: recordId.current });
      }
      setActiveRecordId(recordId.current);
      recordId.current = null;
      fieldId.current = null;
    }
  }, [rows, grid, user?.roles]);

  const handleRecordUpdatedEvent = useCallback(
    (
      gridId: string,
      recordId: string,
      recordSummary: any[],
      fieldIds: string[],
      userId: number,
      userfullName?: string,
      isRetry = false,
    ) => {
      if (!rowsRef.current?.length || !gridRef.current) {
        return;
      }

      if (gridRef.current.id !== gridId) {
        return;
      }

      let recordIndex = rowsRef.current?.findIndex(r => r.id === recordId || r.revisionRecords?.some(rr => rr.id === recordId));
      if (recordIndex === undefined || recordIndex < 0) {
        if (gridRef.current.isRevision) {
          recordIndex = rowsRef.current?.findIndex(r => recordSummary.every(s => r.cells[s.fieldId].value === s.value));
          if (recordIndex >= 0) {
            loadRecords();
          }
        }
        if (!isRetry) {
          storedRecordUpdatedEvents.current.push({ gridId, recordId, recordSummary, fieldIds, userId, userfullName });
        }
        return;
      }

      // const fields = gridRef.current.fields?.filter(f => fieldIds?.some(fi => fi === f?.id));

      //TODO - includeTasks must be conditional.
      const queryParams = [`gridId=${props.gridId || id}`, `rowId=${recordId}`, 'includeTasks=true'];

      const queryParamsStr = queryParams.join('&');
      apiClient
        .get(`records/record?${queryParamsStr}`)
        .then((response: any) => {
          const responseRecord = {
            ...(response.data as GridRow),
            children: response.data.hasChildRows ? [] : undefined,
            revisionRecords: response.data.hasOlderRevisions ? [] : undefined,
          };
          const subRevision = !!responseRecord.latestRevisionRecordId && responseRecord.latestRevisionRecordId !== responseRecord.id;
          let recordIndex: number | undefined;

          if (subRevision) {
            recordIndex = rowsRef.current?.findIndex(r => r.id === responseRecord.latestRevisionRecordId);
          } else {
            recordIndex = rowsRef.current?.findIndex(r => r.id === recordId);
          }

          if (recordIndex === undefined || recordIndex < 0) {
            return;
          }

          const newRows = [...(rowsRef.current as GridRow[])];

          if (subRevision) {
            const subRecordIndex = newRows[recordIndex].revisionRecords?.findIndex((r: any) => r.id === recordId);
            if (subRecordIndex === undefined || subRecordIndex < 0) {
              return;
            }
            newRows[recordIndex].revisionRecords![subRecordIndex] = responseRecord;
          } else {
            let subRevisionRecords: any;
            if (newRows[recordIndex].revisionRecords?.length) {
              subRevisionRecords = [...newRows[recordIndex].revisionRecords!];
            }
            newRows[recordIndex] = responseRecord;
            if (subRevisionRecords) {
              newRows[recordIndex].revisionRecords = subRevisionRecords;
            }
          }

          setRows(newRows);
          rowsRef.current = newRows;

          // if (fields?.length && userId !== user?.id) {
          //   const idFieldValues = gridRef.current?.fields
          //     ?.filter(f => f.isIdentifier)
          //     ?.map(f => responseRecord.cells[f.id].value)
          //     ?.filter(v => !!v);

          //   if (!idFieldValues?.length) {
          //     return;
          //   }

          //   notification.info({
          //     message: `${idFieldValues.join(', ')} değerli kayıtta ${fields?.map(f => f.name).join(', ')} değerleri ${
          //       !!userfullName ? userfullName + ' tarafından ' : ''
          //     }değiştirildi.`,
          //     placement: 'bottomRight',
          //   });
          // }
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        });
    },
    [id, loadRecords, props.gridId],
  );

  const handleCellUpdatedEvent = useCallback(
    (gridId: string, recordId: string, fieldId: string, value: any, valueRef?: string, errors?: string[]) => {
      if (!rowsRef.current?.length || !gridRef.current) {
        return;
      }

      if (gridRef.current.id !== gridId) {
        return;
      }

      let recordIndex = rowsRef.current?.findIndex(r => r.id === recordId);
      if (recordIndex === undefined || recordIndex < 0) {
        return;
      }

      const newRows = [...(rowsRef.current as GridRow[])];

      if (!Object.keys(newRows[recordIndex].cells)?.includes(fieldId)) {
        return;
      }

      if (newRows[recordIndex].cells[fieldId].value === value) {
        return;
      }

      newRows[recordIndex].cells[fieldId].value = value;
      newRows[recordIndex].cells[fieldId].valueRef = valueRef;
      newRows[recordIndex].cells[fieldId].errors = errors || [];

      setRows(newRows);
      rowsRef.current = newRows;
    },
    [],
  );

  useEffect(() => {
    if (!retryUpdatedEventInterval?.current) {
      retryUpdatedEventInterval.current = setInterval(() => {
        if (!storedRecordUpdatedEvents?.current?.length) {
          return;
        }
        for (const e of storedRecordUpdatedEvents.current) {
          handleRecordUpdatedEvent(e.gridId, e.recordId, e.recordSummary, e.fieldIds, e.userId, e.userfullName, true);
          storedRecordUpdatedEvents.current = storedRecordUpdatedEvents?.current?.filter(ev => ev.recordId !== e.recordId);
        }
      }, 5000);
    }
    return () => {
      if (retryUpdatedEventInterval?.current) {
        clearInterval(retryUpdatedEventInterval?.current);
      }
    };
  }, [handleRecordUpdatedEvent]);

  useEffect(() => {
    if (!grid) {
      return;
    }

    const key = 'gridPage-' + props.gridId + '-' + generateRandomString();

    addSocketHandler('record-updated', key, (data: any) => {
      handleRecordUpdatedEvent(data.gridId, data.recordId, data.recordSummary, data.fieldIds, data.userId, data.userfullName);
    });

    addSocketHandler('cell-updated', key, (data: any) => {
      handleCellUpdatedEvent(data.gridId, data.recordId, data.fieldId, data.value, data.valueRef, data.errors);
    });

    return () => {
      removeSocketHandler('record-updated', key);
      removeSocketHandler('cell-updated', key);
    };
  }, [handleRecordUpdatedEvent, handleCellUpdatedEvent, grid, props.gridId]);

  const loadGridLayouts = useCallback(() => {
    apiClient
      .get(`grid-layouts?gridId=${props.gridId || id}`)
      .then((response: any) => {
        setGridLayouts(response.data || []);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [id, props.gridId]);

  const loadIntegrations = useCallback(() => {
    if (!grid?.id) {
      return;
    }
    apiClient
      .get(`integrations?type=EXPORT`)
      .then(response => {
        if (response.data) {
          let responseData = response.data as Integration[];
          responseData = responseData?.filter(i => i.primaryGridId === grid?.id);
          setIntegrations(responseData);
        } else {
          setIntegrations(undefined);
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [grid?.id]);

  useEffect(() => {
    loadGridLayouts();
  }, [loadGridLayouts]);

  useEffect(() => {
    if (!grid || defaultGridLayout) {
      return;
    }

    const sortedFields = grid.fields.sort((f1, f2) => (f1.isIdentifier && !f2.isIdentifier ? -1 : 1));

    const defaultGridLayoutColumns = sortedFields.map((field: Field) => {
      const gridLayoutColumn = new GridLayoutColumn();
      gridLayoutColumn.fieldId = field.id;
      gridLayoutColumn.isFixed = field.isIdentifier;
      const columnWidth = (field.displayName || field.name).length * 10 + 40;
      gridLayoutColumn.width = columnWidth < 150 ? 150 : columnWidth;
      return gridLayoutColumn;
    });

    const defaultView = new GridLayout();
    defaultView.id = 'default';
    defaultView.name = 'Varsayılan';
    defaultView.columns = defaultGridLayoutColumns;

    setDefaultGridLayout(defaultView);
  }, [defaultGridLayout, grid]);

  useEffect(() => {
    if (!gridLayouts || !defaultGridLayout || defaultGridLayout?.isSelected || activeColumns?.length) {
      return;
    }

    const selectedGridLayout = gridLayouts?.find(gv => gv.isSelected);
    if (!selectedGridLayout) {
      setDefaultGridLayout({ ...defaultGridLayout, isSelected: true });
    }
    let columns = selectedGridLayout?.columns || defaultGridLayout.columns;
    columns = columns.filter(c => grid?.fields.some(f => f.id === c.fieldId));
    setActiveColumns(columns);
  }, [activeColumns?.length, defaultGridLayout, grid?.fields, gridLayouts]);

  useEffect(() => {
    if (selectable && selectedAll && rows?.length) {
      setSelectedRows(rows);
    }
  }, [rows, selectable, selectedAll]);

  const onCellSaved = useCallback(
    (row: GridRow) => {
      if (!rows) {
        return;
      }

      const newRows = [...rows];
      let index = newRows.findIndex(r => r.id === row.id);

      if (index < 0) {
        index = newRows.findIndex(r => r.id === row.latestRevisionRecordId);
        if (index < 0) {
          return;
        }
      }

      if (!row.latestRevisionRecordId || row.latestRevisionRecordId === row.id) {
        let revisionRecords: GridRow[] | undefined;
        if (rows[index].revisionRecords?.length) {
          revisionRecords = [...rows[index].revisionRecords!];
        }

        const rowWithKey = {
          ...row,
          key: row.id,
          children: row.hasChildRows ? [] : undefined,
          revisionRecords,
        };

        newRows[index] = rowWithKey;
      } else {
        const revisionIndex = newRows[index].revisionRecords?.findIndex(r => r.id === row.id);

        if (revisionIndex === undefined || revisionIndex < 0) {
          return;
        }

        const revisionRecordWithKey = {
          ...row,
          key: row.id,
          children: row.hasChildRows ? [] : undefined,
        };

        newRows[index].revisionRecords![revisionIndex] = revisionRecordWithKey;
      }
      setRows(newRows);
      rowsRef.current = newRows;

      if (props.onChange) {
        props.onChange();
      }
    },
    [props, rows],
  );

  const onSubGridChanged = loadRecords;

  const onOperationStarted = () => {
    setHasActiveOperation(true);
  };

  const onOperationEnded = () => {
    setHasActiveOperation(false);
  };

  useEffect(() => {
    if (!activeColumns || !grid || !grid.fields?.length) {
      return;
    }

    const getColumnSearchProps = (dataIndex: string, dataType?: string) => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <GridColumnFilter
          fieldId={dataIndex}
          gridId={grid.id}
          dataType={dataType}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined
          style={{
            padding: '4px',
            borderRadius: '2px',
            backgroundColor: filtered ? '#1890ff' : undefined,
            color: filtered ? '#ffffff' : undefined,
          }}
        />
      ),
    });

    const handleResize: Function = (index: number) => (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      const newColumns = [...activeColumns!];
      newColumns[index] = {
        ...newColumns[index],
        width: size.width,
      };
      setActiveColumns([...newColumns]);
    };

    const columns: any[] = [];
    for (let i = 0; i < activeColumns.length; i++) {
      const activeColumn = activeColumns[i];
      const field = grid?.fields?.find(f => f.id === activeColumn?.fieldId);

      if (!field) {
        continue;
      }

      const lastUpdateFilterValue = dataSetFiltersRef.current?.lastUpdate;
      const lastUpdateHighlightDate = !!lastUpdateFilterValue
        ? moment(new Date())
            .subtract(
              lastUpdateFilterValue === 'TWO_WEEKS' ? 2 : 1,
              lastUpdateFilterValue === 'ONE_DAY'
                ? 'day'
                : lastUpdateFilterValue === 'ONE_WEEK'
                ? 'week'
                : lastUpdateFilterValue === 'TWO_WEEKS'
                ? 'weeks'
                : lastUpdateFilterValue === 'ONE_MONTH'
                ? 'month'
                : 'year',
            )
            .toDate()
        : undefined;

      // const columnWidth = (field.displayName || field.name).length * 10 + 40;

      const column = {
        title: field.displayName || field.name,
        dataIndex: ['cells', field.id],
        key: field.id,
        fixed: field.isIdentifier || activeColumn.isFixed ? 'left' : undefined,
        sorter: true,
        filteredValue: !!dataElementFilters ? dataElementFilters[field.id] || null : null,
        width:
          field.dataType === 'SUBGRID'
            ? grid.subGrids
                ?.find(g => g.id === field.subGridId)
                ?.fields?.map(f => (f.displayName || f.name).length * 10 + 20)
                .reduce((prev, curr) => prev + curr)
            : activeColumn.width,
        ...getColumnSearchProps(field.id, field.dataType),
        className: 'ant-table-cell-align-top',
        render: (cell: GridCell, row: GridRow) => (
          <GridCellContent
            grid={grid}
            field={field}
            rowId={row.id}
            cell={cell}
            isObsolete={row.isObsolete}
            showTasks={showTasks}
            hasActiveOperation={hasActiveOperation}
            isEllipsis={true}
            lastUpdateHighlightDate={lastUpdateHighlightDate}
            onOperationStarted={onOperationStarted}
            onOperationEnded={onOperationEnded}
            onCellClick={openCellDetailsModal}
            onSaved={onCellSaved}
            onSubGridChanged={onSubGridChanged}
          />
        ),
        onHeaderCell: (column: any) => ({
          width: column.width,
          onResize: handleResize(i),
        }),
      };
      if (field.isIdentifier) {
        const idColumns = columns.filter(c => grid.fields.find(f => f.id === c.key)?.isIdentifier);
        columns.splice(idColumns.length, 0, column);
      } else {
        columns.push(column);
      }
    }

    const onSDSActionClick = (e: any, id: string, isEn: boolean) => {
      let templateName = '';
      let settings = {};

      if (e.key === 'sds') {
        templateName = !isEn ? 'SDS' : 'EnSDS';
      } else if (e.key === 'minisds') {
        templateName = 'MiniSDS';
        settings = { ktFontSize: '8', edFontSize: '8', mdFontSize: '8' };
      }

      setRowsLoading(true);

      apiClient
        .post(`/records/record/export-pdf`, {
          templateName: templateName,
          rowId: id,
          gridId: grid.id,
          settings: settings,
        })
        .then(async (response: any) => {
          const responseData = response.data;
          const buffer = responseData.file.data;
          const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.setAttribute('download', responseData.fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          setRowsLoading(false);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        });
    };

    const idColumn = {
      title: '',
      dataIndex: 'id',
      width: 80 + (grid.isRevision ? 25 : 0) + (grid.isRevision ? 25 : 0) + (deepestLevel - 1) * 25,
      key: 'id',
      fixed: 'left',
      className: 'ant-table-cell-align-top',
      render: (id: string, row: GridRow) => {
        if (!row?.cells || Object.keys(row?.cells)?.length === 0) {
          return null;
        }
        return (
          <div style={{ display: 'flex', flexFlow: 'row nowrap', justifyContent: 'space-between' }}>
            <div>
              <Button type="default" shape="circle" size="small" icon={<ZoomInOutlined />} onClick={() => openRowDetailsModal(row)} />
            </div>
            <div>
              {(() => {
                const isSDS = String(grid.name).includes('Safety Data Sheet');
                const isEn = String(grid.name).endsWith('- EN');

                if (isSDS) {
                  return (
                    <Dropdown
                      overlay={
                        <Menu onClick={(e: any) => onSDSActionClick(e, id, isEn)}>
                          <Menu.Item key="sds">
                            <Text>SDS raporu oluştur</Text>
                          </Menu.Item>
                          {!isEn && (
                            <Menu.Item key="minisds">
                              <Text>Mini SDS raporu oluştur</Text>
                            </Menu.Item>
                          )}
                        </Menu>
                      }
                      trigger={['click']}
                    >
                      <Button shape="circle" size="small" icon={<FilePdfOutlined />} />
                    </Dropdown>
                  );
                } else {
                  return null;
                }
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {grid.isRevision && !row.isLatestRevision && (dataSetFiltersRef.current?.revisionStatus === 'ALL' || forceAllRevisions) && (
                <Tooltip title="Daha yeni bir revizyon mevcut.">
                  <Text type="warning">
                    <WarningOutlined />
                  </Text>
                </Tooltip>
              )}
              {row.valueTaskCount > 0 && <CellStatusIndicator color="gold" count={row.valueTaskCount} />}
              {Object.values(row.cells).some(c => c.errors?.filter(x => x !== 'Onaylanmamış değer').length) && (
                <CellStatusIndicator
                  color="red"
                  count={Object.values(row.cells).reduce(
                    (acc, c) => acc + (c.errors?.filter(x => x !== 'Onaylanmamış değer').length || 0),
                    0,
                  )}
                />
              )}
              {row.isReadOnly && <LockOutlined style={{ marginLeft: '0.25rem' }} />}
              {row.isPassive && <DisconnectOutlined style={{ marginLeft: '0.25rem' }} />}
              {row.hasCustomIcon && <BulbOutlined style={{ marginLeft: '0.25rem' }} />}
              {row.recordCommentCount > 0 && <CommentOutlined style={{ marginLeft: '0.25rem' }} />}
              {row.recordStatus === 'PROCESSING' && <LoadingOutlined style={{ marginLeft: '0.25rem' }} />}
            </div>
          </div>
        );
      },
    };
    columns.unshift(idColumn);

    if (!grid.parentFieldId || showAuditColumns) {
      if (showAuditColumns) {
        const createdByColumn = {
          title: 'Oluşturan',
          dataIndex: ['createdBy', 'fullName'],
          key: 'MDM_CREATED_BY',
          width: 200,
        };
        columns.push(createdByColumn);
      }

      const createdAtColumn = {
        title: 'Oluşturma',
        dataIndex: 'createdAt',
        key: 'MDM_CREATED_AT',
        sorter: true,
        filteredValue: !!dataElementFilters ? dataElementFilters['MDM_CREATED_AT'] || null : null,
        width: 200,
        ...getColumnSearchProps('MDM_CREATED_AT', 'DATETIME'),
        className: 'ant-table-cell-align-top',
        render: (createdAt: string) => moment(createdAt).format('DD.MM.yyyy HH:mm:ss'),
      };
      columns.push(createdAtColumn);

      const updatedAtColumn = {
        title: 'Son güncelleme',
        dataIndex: 'updatedAt',
        key: 'MDM_UPDATED_AT',
        sorter: true,
        filteredValue: !!dataElementFilters ? dataElementFilters['MDM_UPDATED_AT'] || null : null,
        width: 200,
        ...getColumnSearchProps('MDM_UPDATED_AT', 'DATETIME'),
        className: 'ant-table-cell-align-top',
        render: (updatedAt: string) => moment(updatedAt).format('DD.MM.yyyy HH:mm:ss'),
      };
      columns.push(updatedAtColumn);
    }

    setTableColumns(columns);
    tableColumnsRef.current = columns;
  }, [
    Text,
    activeColumns,
    deepestLevel,
    forceAllRevisions,
    grid,
    hasActiveOperation,
    onCellSaved,
    onSubGridChanged,
    showTasks,
    dataElementFilters,
    showAuditColumns,
  ]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    if (!id) {
      return;
    }
    loadGrid();
    loadRecords();
  }, [id, loadGrid, loadRecords]);

  const onRefreshClick = () => {
    loadRecords();
  };

  const openAddRowModal = () => {
    setAddRowModalOpen(true);
  };

  const closeAddRowModal = () => {
    setAddRowModalOpen(false);
  };

  const onRowAdded = () => {
    closeAddRowModal();
    closeRowDetailsModal();
    loadRecords();
    if (props.onChange) {
      props.onChange();
    }
  };

  const openImportRowsModal = () => {
    setImportRowsModalOpen(true);
  };

  const closeImportRowsModal = () => {
    setImportRowsModalOpen(false);
  };

  const openExcelExportModal = () => {
    setExcelExportModalOpen(true);
  };

  const closeExcelExportModal = () => {
    setExcelExportModalOpen(false);
  };

  const onRowsImported = () => {
    closeImportRowsModal();
    loadRecords();
  };

  const toggleSelectable = () => {
    if (!selectable) {
      setSelectable(true);
      loadIntegrations();
    } else {
      setSelectable(false);
      setSelectedRows(undefined);
    }
  };

  const exportSelectedRows = (integrationId: string) => {
    console.log('exportSelectedRows - Start', { selectedRowsCount: selectedRows?.length, selectedAll });
    if (!selectedRows?.length) {
      return;
    }

    const integration = integrations?.find(i => i.id === integrationId);
    if (!integration) {
      message.error('Entegrasyon bulunamadı.');
      return;
    }

    const recordCount = selectedAll ? tableTotal : selectedRows?.length || 0;

    Modal.confirm({
      title: 'Aktarım',
      content: `${recordCount} kayıt ${integration.name} aktarımı ile aktarılacak, devam etmek istiyor musunuz?`,
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: () => {
        let body: any = {
          gridId: id,
        };

        const conditions: Condition = {
          logic: 'AND',
          items: [],
        };

        let showAllRevisions = false;

        if (selectedAll) {
          if (dataElementFilters) {
            for (let filterKey in dataElementFilters) {
              if (!dataElementFilters[filterKey]) {
                continue;
              }
              const filter = dataElementFilters[filterKey];

              const basicSearchFilterValue = filter[0];
              const isNullFilterValue = filter[1];
              const isNotFilterValue = filter[2];
              const isTaskFilterValue = filter[3];
              const isErrorFilterValue = filter[4];
              const advancedFilterValue = filter[5];
              const distinctFilterValue = filter[6];

              if (
                basicSearchFilterValue !== undefined &&
                basicSearchFilterValue !== null &&
                basicSearchFilterValue.toString().trim() !== ''
              ) {
                conditions.items.push({
                  objectType: 'dataElement',
                  objectId: filterKey,
                  operator: 'search',
                  value: basicSearchFilterValue,
                });
              }

              if (isNullFilterValue === '1') {
                conditions.items.push({
                  objectType: 'dataElement',
                  objectId: filterKey,
                  operator: 'isNull',
                  value: true,
                });
              }

              if (isNotFilterValue === '1') {
                conditions.items.push({
                  objectType: 'dataElement',
                  objectId: filterKey,
                  operator: 'isNotNull',
                  value: true,
                });
              }

              if (isTaskFilterValue === '1') {
                conditions.items.push({
                  objectType: 'dataElement',
                  objectId: filterKey,
                  operator: 'tasks',
                  value: 'true',
                });
              }

              if (isErrorFilterValue === '1') {
                conditions.items.push({
                  objectType: 'dataElement',
                  objectId: filterKey,
                  operator: 'errors',
                  value: 'true',
                });
              }

              if (distinctFilterValue) {
                let values = JSON.parse(distinctFilterValue)?.map((v: string | null) => (v === null ? '###null###' : v));
                conditions.items.push({
                  objectType: 'dataElement',
                  objectId: filterKey,
                  operator: 'in',
                  value: values,
                });
              }

              if (advancedFilterValue) {
                const advancedFilters = JSON.parse(advancedFilterValue);
                conditions.items.push(advancedFilters);
              }

              showAllRevisions = true;
            }
          }
          if (dataSetFiltersRef.current) {
            if (dataSetFiltersRef.current.activeStatus && dataSetFiltersRef.current.activeStatus !== 'ACTIVE') {
              conditions.items.push({
                objectType: 'record',
                objectId: 'MDM_RECORD',
                operator: 'activeStatus',
                value: dataSetFiltersRef.current.activeStatus === 'INACTIVE' ? 'inactive' : 'all',
              });
              showAllRevisions = true;
            } else {
              conditions.items.push({
                objectType: 'record',
                objectId: 'MDM_RECORD',
                operator: 'activeStatus',
                value: 'active',
              });
            }

            if (dataSetFiltersRef.current.taskStatus && dataSetFiltersRef.current.taskStatus !== 'ALL') {
              conditions.items.push({
                objectType: 'record',
                objectId: 'MDM_RECORD',
                operator: dataSetFiltersRef.current.taskStatus === 'WITH_MY_TASKS' ? 'myTasks' : 'tasks',
                value: dataSetFiltersRef.current.taskStatus === 'WITHOUT_TASKS' ? 'false' : 'true',
              });
              showAllRevisions = true;
            }

            if (dataSetFiltersRef.current.errorStatus && dataSetFiltersRef.current.errorStatus !== 'ALL') {
              conditions.items.push({
                objectType: 'record',
                objectId: 'MDM_RECORD',
                operator: 'errors',
                value: dataSetFiltersRef.current.errorStatus === 'WITH_ERRORS' ? 'true' : 'false',
              });
              showAllRevisions = true;
            }

            if (dataSetFiltersRef.current.lastUpdate && dataSetFiltersRef.current.lastUpdate !== 'ALL') {
              let lastUpdateValue: string = '';
              let subtractDays = 0;
              switch (dataSetFiltersRef.current.lastUpdate) {
                case 'ONE_DAY':
                  subtractDays = 1;
                  break;
                case 'ONE_WEEK':
                  subtractDays = 7;
                  break;
                case 'TWO_WEEKS':
                  subtractDays = 14;
                  break;
                case 'ONE_MONTH':
                  subtractDays = 30;
                  break;
                case 'ONE_YEAR':
                  subtractDays = 365;
                  break;
              }
              if (subtractDays > 0) {
                lastUpdateValue = moment.utc().subtract(subtractDays, 'days').startOf('day').format('YYYY-MM-DD');
              }

              conditions.items.push({
                objectType: 'record',
                objectId: 'MDM_RECORD',
                operator: 'lastUpdatedAfter',
                value: lastUpdateValue,
              });
            }

            if (dataSetFiltersRef.current.revisionStatus || showAllRevisions) {
              if (
                dataSetFiltersRef.current.revisionStatus === 'PREVIOUS' ||
                dataSetFiltersRef.current.revisionStatus === 'ALL' ||
                showAllRevisions
              ) {
                conditions.items.push({
                  objectType: 'record',
                  objectId: 'MDM_RECORD',
                  operator: 'revisions',
                  value: dataSetFiltersRef.current.revisionStatus === 'ALL' || showAllRevisions ? 'all' : 'previous',
                });
                showAllRevisions = true;
              } else {
                conditions.items.push({
                  objectType: 'record',
                  objectId: 'MDM_RECORD',
                  operator: 'revisions',
                  value: 'LATEST',
                });

                if (dataSetFiltersRef.current.revisionStatus === 'NESTED') {
                  body.includeRevisions = true;
                }

                showAllRevisions = false;
              }
            }

            if (dataSetFiltersRef.current.recordStatus && dataSetFiltersRef.current.recordStatus !== 'ALL') {
              conditions.items.push({
                objectType: 'record',
                objectId: 'MDM_RECORD',
                operator: 'processStatus',
                value: dataSetFiltersRef.current.recordStatus === 'PROCESSING' ? 'processing' : 'ready',
              });
            }
          }
        } else {
          conditions.items.push({
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'recordIds',
            value: selectedRows.map(r => r.id),
          });
        }

        body.conditions = conditions;
        setExporting(true);
        console.log('exportSelectedRows - Post to API', { body });

        apiClient
          .post(`integrations/${integrationId}/execute`, body)
          .then((response: any) => {
            console.log('exportSelectedRows - API Response', { response: response.data });
            const execution = response.data as IntegrationExecution;
            if (execution.hasError) {
              message.error(execution.errorMessage);
            } else {
              if (execution.recordCount) {
                message.success(
                  execution.recordCount + '/' + (selectedAll ? tableTotal : selectedRows?.length || 0) + ' adet kayıt başarıyla aktarıldı',
                );
              } else {
                message.info('Herhangi bir kayıt aktarımı yapılmadı.');
              }
            }
          })
          .catch((reason: any) => {
            console.log('exportSelectedRows - API Error', { error: reason });
            apiErrorMessage(reason);
          })
          .finally(() => setExporting(false));
      },
    });
  };

  const onActionsMenuClick = (e: any) => {
    if (e.key === 'excelExport') {
      openExcelExportModal();
    }
    if (e.key === 'import') {
      openImportRowsModal();
    } else if (e.key === 'selectable') {
      toggleSelectable();
    } else if ((e.key as string).startsWith('export_')) {
      const integrationId = (e.key as string).replace('export_', '');
      exportSelectedRows(integrationId);
    }
  };

  const actionsMenu = (
    <Menu onClick={onActionsMenuClick}>
      {userCanExportToExcel && <Menu.Item key="excelExport">Excel'e aktar</Menu.Item>}
      {user?.roles?.some(r => r.name === 'Misafir') ? null : <Menu.Item key="import">Toplu yeni kayıt</Menu.Item>}
      <Menu.Divider />
      <Menu.Item key="selectable" icon={selectable ? <CheckSquareOutlined /> : <BorderOutlined />}>
        <span>Kayıt seçimi</span>
        {selectable && <span> ({selectedAll ? tableTotal : selectedRows?.length ? selectedRows?.length : 0})</span>}
      </Menu.Item>
      {selectedRows?.length && integrations?.length && selectedRows?.every(r => !r.isObsolete && !r.isPassive) && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup title="Dışa aktarım">
            {integrations?.map(i => (
              <Menu.Item key={'export_' + i.id}>{i.name}</Menu.Item>
            ))}
          </Menu.ItemGroup>
        </>
      )}
    </Menu>
  );

  const openRowDetailsModal = (row: GridRow) => {
    if (row.recordStatus === 'PROCESSING' && !hasViewProcessingRecordsPermission(user, grid?.id)) {
      message.info('Bu kayıt henüz işleniyor, lütfen daha sonra tekrar deneyin.');
      return;
    }

    setRowDetailsModalOpen(row);
  };

  const closeRowDetailsModal = () => {
    setRowDetailsModalOpen(undefined);
  };

  const onFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const onFiltersChange = (newFilterValues: any) => {
    if (
      (!newFilterValues?.revisionStatus || newFilterValues?.revisionStatus === 'LATEST') &&
      dataSetFiltersRef.current?.revisionStatus !== newFilterValues?.revisionStatus
    ) {
      expandedRowKeys.current = [];
      setExpandedRowKeysUi(expandedRowKeys.current);
    }
    dataSetFiltersRef.current = newFilterValues;
    loadRecords();
  };

  const onRowSaved = (row: GridRow) => {
    if (!rows) {
      return;
    }

    const newRows = [...rows];
    let index = newRows.findIndex(r => r.id === row.id);

    if (index < 0) {
      index = newRows.findIndex(r => r.id === row.latestRevisionRecordId);
    }

    if (index > -1) {
      if (!row.latestRevisionRecordId || row.latestRevisionRecordId === row.id) {
        const rowWithKey = {
          ...row,
          key: row.id,
          children: row.hasChildRows ? [] : undefined,
          revisionRecords:
            grid?.isRevision && !row.revisionRecords?.length && dataSetFiltersRef.current?.revisionStatus !== 'ALL' && !forceAllRevisions
              ? row.hasOlderRevisions
                ? []
                : undefined
              : row.revisionRecords,
        };

        newRows[index] = rowWithKey;
      } else {
        const revisionIndex = newRows[index].revisionRecords?.findIndex(r => r.id === row.id);

        if (!revisionIndex || revisionIndex < 0) {
          return;
        }

        const revisionRecordWithKey = {
          ...row,
          key: row.id,
          children: row.hasChildRows ? [] : undefined,
        };

        newRows[index].revisionRecords![revisionIndex] = revisionRecordWithKey;
      }
      setRows(newRows);
      rowsRef.current = newRows;
    } else {
      const newRows = [...rows];
      newRows.unshift(row);
      setRows(newRows);
      rowsRef.current = newRows;
    }

    if (props.onChange) {
      props.onChange();
    }
  };

  const onRowDelete = () => {
    closeRowDetailsModal();
    loadRecords();

    if (props.onChange) {
      props.onChange();
    }
  };

  const openCellDetailsModal = (field: Field, rowId: string) => {
    let record = rowsRef.current?.find(r => r.id === rowId);

    if (record?.recordStatus === 'PROCESSING' && !hasViewProcessingRecordsPermission(user, grid?.id)) {
      message.info('Bu kayıt henüz işleniyor, lütfen daha sonra tekrar deneyin.');
      return;
    }

    if (!record && rowsRef.current?.length) {
      const revisionRecords = rowsRef.current.flatMap(r => r.revisionRecords);
      for (const revisionRecord of revisionRecords) {
        if (revisionRecord?.id === rowId) {
          record = revisionRecord;
        }
      }
    }
    if (!user?.roles?.some(r => r.name === 'Misafir')) {
      setCellDetailsModalOpen({ row: record, grid: grid, gridId: field.gridId, field, rowId });
    }
    setActiveRecordId(rowId);
  };

  const closeCellDetailsModal = () => {
    setCellDetailsModalOpen(undefined);
  };

  const onShowTasksClick = () => {
    setShowTasks(!showTasks);
  };

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    if (tableCurrent === (pagination.current || 1) && tablePageSize === pagination.pageSize) {
      setSelectedAll(false);
      setSelectedRows(undefined);
    }
    setTableCurrent(pagination.current || 1);
    tableCurrentRef.current = pagination.current || 1;
    setTablePageSize(pagination.pageSize);
    tablePageSizeRef.current = pagination.pageSize;
    setTableOrderBy(sorter.columnKey);
    tableOrderByRef.current = sorter.columnKey;
    setTableOrderDir(!sorter.order ? undefined : sorter.order === 'descend' ? 'DESC' : 'ASC');
    tableOrderDirRef.current = !sorter.order ? undefined : sorter.order === 'descend' ? 'DESC' : 'ASC';
    let sanitizedFilters: any;
    if (Object.values(filters).some(v => !!v)) {
      sanitizedFilters = filters;
    }
    setDataElementFilters(sanitizedFilters);
    dataElementFiltersRef.current = sanitizedFilters;
  };

  const onClearFiltersClick = () => {
    let loadRequired = false;
    if (
      !dataElementFiltersRef.current &&
      !tableOrderByRef.current &&
      !tableOrderDirRef.current &&
      tableCurrentRef.current === 1 &&
      dataSetFiltersRef.current
    ) {
      loadRequired = true;
    }
    dataSetFiltersRef.current = undefined;
    setDataElementFilters(undefined);
    dataElementFiltersRef.current = undefined;
    setTableOrderBy(undefined);
    tableOrderByRef.current = undefined;
    setTableOrderDir(undefined);
    tableOrderDirRef.current = undefined;
    setTableCurrent(1);
    tableCurrentRef.current = 1;
    setFilterResetTrigger(filterResetTrigger => filterResetTrigger + 1);

    if (loadRequired) {
      loadRecords();
    }
  };

  const rowSelection = {
    fixed: true,
    onSelect: (selectedTableRow: GridRow, selected: boolean) => {
      setSelectedAll(false);
      if (!selected) {
        const currentSelectedRows = selectedRows?.filter(r => r.id !== selectedTableRow.id);
        setSelectedRows(currentSelectedRows);
      } else {
        const currentSelectedRows = selectedRows?.filter(r => r.id !== selectedTableRow.id);
        setSelectedRows([...(currentSelectedRows || []), selectedTableRow]);
      }
    },
    onSelectAll: (selected: any) => {
      setSelectedAll(selected);
      if (!selected) {
        setSelectedRows(undefined);
      }
    },
  };

  const onExpand = (expanded: any, record: any) => {
    if (grid?.isRevision) {
      onRevisionExpand(expanded, record);
    }
  };

  const onRevisionExpand = (expanded: any, record: any) => {
    if (!expanded || !rows || !record.isLatestRevision) {
      if (expandedRowKeys.current?.length) {
        const newExpandedRowKeys = expandedRowKeys.current.filter(k => k !== record.id);
        expandedRowKeys.current = newExpandedRowKeys || [];
        setExpandedRowKeysUi(expandedRowKeys.current);
      }
      return;
    }

    if (record.revisionRecords?.length) {
      if (expandedRowKeys.current?.length) {
        expandedRowKeys.current = [...expandedRowKeys.current, record.id];
      } else {
        expandedRowKeys.current = [record.id];
      }
      setExpandedRowKeysUi(expandedRowKeys.current);
      return;
    }

    setRowsLoading(true);

    const body: any = {
      gridId: id,
      revisionsFor: [record.id],
      limit: 0,
      conditions: {
        logic: 'AND',
        items: [
          {
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'activeStatus',
            value: 'active',
          },
        ],
      },
    };

    if (showTasks) {
      body.includeTasks = true;
    }

    apiClient
      .post(`records/records/query`, body)
      .then((response: any) => {
        const responseRows = response.data.records;

        if (!responseRows?.length) {
          return;
        }

        if (expandedRowKeys.current?.length) {
          expandedRowKeys.current = [...expandedRowKeys.current, record.id];
        } else {
          expandedRowKeys.current = [record.id];
        }
        setExpandedRowKeysUi(expandedRowKeys.current);

        const newRows = [...rows];
        setRevisionRecords(
          newRows,
          record.id,
          responseRows.filter((r: any) => !r.isLatestRevision),
        );
        setRows(newRows);
        rowsRef.current = newRows;
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setRowsLoading(false));
  };

  const setRevisionRecords = (rows: any[], parentRowId: string, revisionRecords: any[]) => {
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].id === parentRowId) {
        rows[i].revisionRecords = revisionRecords;
        setDeepestLevel(1);
      }
    }
  };

  const onGridLayoutSaved = () => {
    loadGridLayouts();
  };

  return (
    <>
      <PageHeader
        title={grid?.name}
        subTitle={grid?.name}
        onBack={onBackClick}
        extra={[
          selectable && (
            <span key="selected-rows">
              {selectedAll ? tableTotal + ' satır seçili' : selectedRows?.length ? selectedRows?.length + ' satır seçili' : null}
            </span>
          ),
          (dataElementFilters || dataSetFiltersRef.current) && (
            <Button key="clear-filters" type="link" onClick={onClearFiltersClick}>
              Tüm filtreleri temizle
            </Button>
          ),
          <Button key="filters" onClick={onFiltersToggle} icon={<FilterOutlined />}>
            Filtreleri {filtersOpen ? 'gizle' : 'göster'}
          </Button>,
          !grid?.parentFieldId && !user?.roles?.some(r => r.name === 'Misafir') && (
            <GridLayoutsForm
              fields={grid?.fields}
              defaultGridLayout={defaultGridLayout}
              gridLayouts={gridLayouts}
              activeColumns={activeColumns}
              setActiveColumns={setActiveColumns}
              onGridLayoutSaved={onGridLayoutSaved}
              key="grid-layouts-form"
            />
          ),
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          user?.roles?.some(r => r.name === 'Misafir') ? null : (
            <Button key="tasks" onClick={onShowTasksClick} icon={<EditOutlined />}>
              {showTasks ? 'Düzenlemeyi kapat' : 'Düzenle'}
            </Button>
          ),
          user?.roles?.some(r => r.name === 'Misafir') ? null : (
            <Button
              key="add"
              onClick={openAddRowModal}
              icon={<PlusOutlined />}
              type="primary"
              disabled={rowsLoading || gridLoading || !userCanCreateRecord || parentGridLoading}
            >
              Yeni kayıt
            </Button>
          ),
          <Dropdown key="more" overlay={actionsMenu}>
            <Button type="text" style={{ padding: '0.25rem' }}>
              <EllipsisOutlined style={{ fontSize: '1.25rem', verticalAlign: 'top' }} />
            </Button>
          </Dropdown>,
        ]}
      />
      <Content>
        <FiltersForm
          hidden={!filtersOpen}
          gridId={id}
          isRevisionGrid={grid?.isRevision}
          onChange={onFiltersChange}
          resetTrigger={filterResetTrigger}
          withMyTasks={withMyTasks}
        />
        <Table
          columns={tableColumns}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          dataSource={rows}
          loading={rowsLoading}
          scroll={{ x: '100%', y: 'calc(100vh - 360px' }}
          rowKey={(row: GridRow) => row.id}
          pagination={{
            current: tableCurrent,
            total: tableTotal,
            pageSize: tablePageSize,
            pageSizeOptions: ['10', '25', '50', '100'],
            position: ['bottomCenter'],
            showTotal: (total, range) => {
              let start = range[0];
              if (start < 1) {
                start = 1;
              }
              let end: number = range[1];
              if (end === 0) {
                end = total;
              }
              return `Toplam ${total} kayıt. ${start}-${end} arası gösteriliyor.`;
            },
            showSizeChanger: true,
          }}
          expandable={{
            expandedRowKeys: expandedRowKeysUi,
            onExpand: onExpand,
            childrenColumnName: grid?.isRevision ? 'revisionRecords' : 'children',
          }}
          rowSelection={selectable ? { ...rowSelection, selectedRowKeys: selectedRows?.map(r => r.id) } : undefined}
          onChange={onTableChange}
          size="small"
          bordered
          className="mdm-grid"
          rowClassName={record => {
            const classNames: string[] = [];
            if (record.hasChildRows && !record.parentRowId && expandedRowKeysUi?.some(k => k === record.id)) {
              classNames.push('expanded');
            }
            if (activeRecordId === record.id) {
              classNames.push('active');
            }
            if (record.isPassive) {
              classNames.push('passive');
            }
            return classNames.join(' ');
          }}
        />
        <TaskTypeLegend />
      </Content>
      <FloatingModal
        title="Yeni kayıt"
        visible={addRowModalOpen}
        footer={null}
        onCancel={closeAddRowModal}
        destroyOnClose={true}
        width={'80%'}
        maskClosable={false}
      >
        {
          //CUSTOM DEVELOPMENT
          grid?.code === 'ZZ_MASTAR_KALEM_TABLOSU' ? (
            <ItemJigNewRecordPage grid={grid} onSaved={onRowAdded} />
          ) : (
            <NewGridRowPage grid={grid} onSaved={onRowAdded} rowId={props.rowId} />
          )
        }
      </FloatingModal>
      <FloatingModal
        title="Toplu yeni kayıt"
        visible={importRowsModalOpen}
        footer={null}
        onCancel={closeImportRowsModal}
        destroyOnClose={true}
        maskClosable={false}
      >
        <ImportGridRowsPage grid={grid} onSaved={onRowsImported} />
      </FloatingModal>
      <FloatingModal
        title="Excel'e aktar"
        visible={excelExportModalOpen}
        footer={null}
        onCancel={closeExcelExportModal}
        destroyOnClose={true}
      >
        <ExcelExportPage
          gridId={props.gridId || id}
          parentRecordId={props.rowId}
          dataElementFilters={dataElementFilters}
          dataSetFilters={dataSetFiltersRef.current}
          orderBy={tableOrderBy}
          orderDir={tableOrderDir}
        />
      </FloatingModal>
      <FloatingModal
        title="Kayıt detayları"
        visible={rowDetailsModalOpen}
        footer={null}
        onCancel={closeRowDetailsModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <RowDetailsPage
          grid={grid}
          row={rowDetailsModalOpen}
          onRowAdded={onRowAdded}
          onSaved={onRowSaved}
          onSubGridChanged={onSubGridChanged}
          onDelete={onRowDelete}
        />
      </FloatingModal>
      <FloatingModal
        title="Veri detayları"
        visible={cellDetailsModalOpen}
        footer={null}
        onCancel={closeCellDetailsModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <CellEditPage
          grid={cellDetailsModalOpen?.grid}
          gridId={cellDetailsModalOpen?.gridId}
          record={cellDetailsModalOpen?.row}
          rowId={cellDetailsModalOpen?.rowId}
          field={cellDetailsModalOpen?.field}
          allowRecordWithErrors={grid?.allowRecordWithErrors}
          onSaved={onCellSaved}
        />
      </FloatingModal>
      <FloatingModal
        title="Kayıtlar dışa aktarılıyor"
        visible={exporting}
        footer={null}
        onCancel={undefined}
        destroyOnClose={false}
        maskClosable={false}
        closable={false}
        width={400}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}>
            <Spin size="large" />
          </div>
          {`${selectedAll ? tableTotal : selectedRows?.length || 0} kayıt dışa aktarılıyor, lütfen bekleyiniz.`}
        </div>
      </FloatingModal>
    </>
  );
}

function TaskTypeLegend() {
  return (
    <Space size={'large'} style={{ marginTop: '2rem' }}>
      <div>
        <Space>
          <CellStatusIndicator color="magenta" />
          Zorunlu alan
        </Space>
      </div>
      <div>
        <Space>
          <CellStatusIndicator color="red" />
          Kural hatası
        </Space>
      </div>
      <div>
        <Space>
          <CellStatusIndicator color="lime" />
          Bekleyen kural hatası görevi
        </Space>
      </div>
      <div>
        <Space>
          <CellStatusIndicator color="gold" />
          Bekleyen değişiklik talebi
        </Space>
      </div>
      <div>
        <Space>
          <CellStatusIndicator color="gray" />
          Bekleyen işlem
        </Space>
      </div>
      <div>
        <Space>
          <CellStatusIndicator color="blue" />
          Onaylanmamış değer
        </Space>
      </div>
    </Space>
  );
}

function FiltersForm(props: {
  hidden: boolean;
  resetTrigger: number;
  gridId: string;
  isRevisionGrid?: boolean;
  onChange: (filterValues: any) => void;
  withMyTasks?: any;
}) {
  const { user } = useContext(UserContext);
  const { Option } = Select;
  const [form] = useForm();

  // CUSTOM DEVELOPMENT
  let defaultFormValues = {};
  if (
    (props.gridId === '18c9e515-ab9b-47a4-8169-f974bba5992c' || props.gridId === '8d19a009-e476-4f13-810d-cfc5f920cdc7') &&
    user?.roles?.some(r => r.name === 'Kalite')
  ) {
    defaultFormValues = {
      taskStatus: 'WITH_MY_TASKS',
    };
  }

  if (props.withMyTasks.current === 'true') {
    defaultFormValues = {
      taskStatus: 'WITH_MY_TASKS',
    };
  }

  useEffect(() => {
    form.resetFields();
  }, [form, props.resetTrigger]);

  const onChange = () => {
    const values = form.getFieldsValue();
    props.onChange(values);
  };

  if (props.hidden) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <Form form={form} initialValues={defaultFormValues} layout="inline">
        <Form.Item name="activeStatus">
          <Select allowClear placeholder="Kayıt tipi" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="ACTIVE">Aktifler</Option>
            <Option value="INACTIVE">Inaktifler</Option>
            <Option value="ALL">Tümü</Option>
          </Select>
        </Form.Item>
        <Form.Item name="taskStatus">
          <Select allowClear placeholder="Bekleyen görevler" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="WITH_MY_TASKS">Görevim olanlar</Option>
            <Option value="WITH_TASKS">Görev olanlar</Option>
            <Option value="WITHOUT_TASKS">Görev olmayanlar</Option>
            <Option value="ALL">Tümü</Option>
          </Select>
        </Form.Item>
        <Form.Item name="errorStatus">
          <Select allowClear placeholder="Kural hataları" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="WITH_ERRORS">Hata olanlar</Option>
            <Option value="WITHOUT_ERRORS">Hata olmayanlar</Option>
            <Option value="ALL">Tümü</Option>
          </Select>
        </Form.Item>
        <Form.Item name="lastUpdate">
          <Select allowClear placeholder="Son güncelleme" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="ONE_DAY">1 gün</Option>
            <Option value="ONE_WEEK">1 hafta</Option>
            <Option value="TWO_WEEKS">2 hafta</Option>
            <Option value="ONE_MONTH">1 ay</Option>
            <Option value="ONE_YEAR">1 yıl</Option>
            <Option value="ALL">Tümü</Option>
          </Select>
        </Form.Item>
        {props.isRevisionGrid && (
          <Form.Item name="revisionStatus">
            <Select allowClear placeholder="Revizyonlar" style={{ width: '12rem' }} onChange={onChange}>
              <Option value="LATEST">Son revizyonlar</Option>
              <Option value="PREVIOUS">Eski revizyonlar</Option>
              <Option value="NESTED">Tüm revizyonlar</Option>
              <Option value="ALL">Tüm kayıtlar</Option>
            </Select>
          </Form.Item>
        )}
        <Form.Item name="recordStatus">
          <Select allowClear placeholder="Kayıt durumu" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="READY">Tamamlanmış kayıtlar</Option>
            <Option value="PROCESSING">İşlem devam edenler</Option>
            <Option value="ALL">Tümü</Option>
          </Select>
        </Form.Item>
      </Form>
    </Card>
  );
}

const ResizableTitle = (
  props: React.HTMLAttributes<any> & {
    onResize: (e: React.SyntheticEvent<Element>, data: ResizeCallbackData) => void;
    width: number;
  },
) => {
  const { onResize, width, ...restProps } = props;
  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: '2px',
            height: '100%',
            cursor: 'ew-resize',
            backgroundImage: 'none',
          }}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
    >
      <th {...restProps} />
    </Resizable>
  );
};
